import React, { useEffect, useState, useCallback } from 'react';

const API_BASE = 'http://localhost:8888';

interface TelemetrySummary {
  total_requests: number;
  avg_latency_ms: number;
  total_tokens_in: number;
  total_tokens_out: number;
  successful: number;
  failed: number;
  success_rate: number;
}

interface ProviderRow {
  provider: string;
  requests: number;
  avg_latency_ms: number;
}

interface ModelRow {
  model: string;
  requests: number;
  avg_latency_ms: number;
  tokens_in: number;
  tokens_out: number;
}

interface StatsResponse {
  summary: TelemetrySummary;
  by_model: ModelRow[];
  by_provider: ProviderRow[];
}

interface LimitItem {
  provider: string;
  max_tokens: number;
  max_usd: number;
  reset_period: string;
  rpm: number | null;
  rpd: number | null;
  tpm: number | null;
  tpd: number | null;
  ash: number | null;
  asd: number | null;
}

interface LimitForm {
  provider: string;
  max_tokens: number;
  max_usd: number;
  reset_period: string;
  rpm: string;
  rpd: string;
  tpm: string;
  tpd: string;
  ash: string;
  asd: string;
}

const TENANT = 'TENANT_C';

const KNOWN_PROVIDERS = ['Ollama', 'NVIDIA', 'NV2', 'MistralAI', 'Groq', 'DashScope', 'GPT4Free'];

const numOrNull = (s: string) => (s.trim() === '' ? null : parseInt(s, 10));

export const TelemetryDashboard: React.FC = () => {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [limits, setLimits] = useState<LimitItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [limitForm, setLimitForm] = useState<LimitForm>({
    provider: 'Groq',
    max_tokens: 0,
    max_usd: 0,
    reset_period: 'daily',
    rpm: '30',
    rpd: '1000',
    tpm: '',
    tpd: '',
    ash: '',
    asd: '',
  });

  const getAuthHeaders = () => {
    const apiKey = localStorage.getItem('omnigate_api_key') || '';
    return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` };
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/v1/telemetry/stats?hours=${hours}`, { headers: getAuthHeaders() });
      if (r.ok) {
        const data = await r.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Telemetry fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  const fetchLimits = useCallback(async () => {
    try {
      const adminKey = localStorage.getItem('omnigate_admin_key') || localStorage.getItem('omnigate_api_key') || '';
      const r = await fetch(`${API_BASE}/v1/telemetry/limits?tenant_id=${TENANT}`, {
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey}`, 'X-Admin-Key': adminKey },
      });
      if (r.ok) {
        const data = await r.json();
        setLimits(data.limits || []);
      }
    } catch (e) {
      console.error('Limits fetch failed:', e);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchLimits();
  }, [fetchStats, fetchLimits]);

  const handleSaveLimit = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const adminKey = localStorage.getItem('omnigate_admin_key') || localStorage.getItem('omnigate_api_key') || '';
      const body = {
        tenant_id: TENANT,
        provider: limitForm.provider,
        max_tokens: limitForm.max_tokens,
        max_usd: limitForm.max_usd,
        reset_period: limitForm.reset_period,
        rpm: numOrNull(limitForm.rpm),
        rpd: numOrNull(limitForm.rpd),
        tpm: numOrNull(limitForm.tpm),
        tpd: numOrNull(limitForm.tpd),
        ash: numOrNull(limitForm.ash),
        asd: numOrNull(limitForm.asd),
      };
      const r = await fetch(`${API_BASE}/v1/telemetry/limits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminKey}`, 'X-Admin-Key': adminKey },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setSaveMsg('✅ Zapisano!');
        await fetchLimits();
      } else {
        setSaveMsg(`❌ Błąd: ${r.status}`);
      }
    } catch (e) {
      setSaveMsg('❌ Błąd sieci');
    } finally {
      setSaving(false);
    }
  };

  const s = stats?.summary;
  const byProvider = stats?.by_provider || [];
  const byModel = (stats?.by_model || []).slice(0, 10);
  const maxReq = byProvider.reduce((mx, p) => Math.max(mx, p.requests), 1);

  const PROVIDER_COLORS: Record<string, string> = {
    Groq: '#f97316',
    MistralAI: '#7c3aed',
    NVIDIA: '#22c55e',
    NV2: '#16a34a',
    Ollama: '#3b82f6',
    DashScope: '#ec4899',
    GPT4Free: '#f59e0b',
  };

  const providerColor = (name: string) =>
    PROVIDER_COLORS[name] || '#6b7280';

  const fieldNum = (key: keyof LimitForm) => ({
    type: 'number',
    min: 0,
    value: limitForm[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setLimitForm(prev => ({ ...prev, [key]: e.target.value })),
    style: {
      width: '100%',
      background: '#1e293b',
      color: '#e2e8f0',
      border: '1px solid #334155',
      borderRadius: 6,
      padding: '6px 10px',
      fontSize: 13,
    },
  });

  return (
    <div style={{ background: '#0f172a', color: '#e2e8f0', minHeight: '100%', padding: 20, fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#7dd3fc' }}>📊 Telemetria</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {([6, 24, 72, 168] as const).map(h => (
            <button key={h} onClick={() => setHours(h)} style={{
              padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
              background: hours === h ? '#3b82f6' : '#1e293b',
              color: hours === h ? '#fff' : '#94a3b8',
              border: '1px solid #334155',
            }}>{h === 168 ? '7d' : h === 72 ? '3d' : `${h}h`}</button>
          ))}
          <button onClick={() => { fetchStats(); fetchLimits(); }} style={{
            padding: '4px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
            background: '#22c55e', color: '#fff', border: 'none',
          }}>🔄 Odśwież</button>
        </div>
      </div>

      {loading && <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>Ładowanie…</div>}

      {!loading && s && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Zapytania', value: s.total_requests ?? 0 },
              { label: 'Sukces %', value: `${((s.success_rate ?? 0) * 100).toFixed(1)}%` },
              { label: 'Avg Latency', value: `${Math.round(s.avg_latency_ms ?? 0)} ms` },
              { label: 'Tokeny IN', value: (s.total_tokens_in ?? 0).toLocaleString() },
              { label: 'Tokeny OUT', value: (s.total_tokens_out ?? 0).toLocaleString() },
              { label: 'Błędy', value: s.failed ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: '#1e293b', borderRadius: 10, padding: '14px 16px', textAlign: 'center', border: '1px solid #334155' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#7dd3fc' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* By Provider Bar Chart */}
          {byProvider.length > 0 && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #334155' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#94a3b8' }}>Zapytania wg dostawcy</h3>
              {byProvider.map(p => (
                <div key={p.provider} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 12 }}>
                    <span style={{ color: providerColor(p.provider), fontWeight: 600 }}>{p.provider || 'Unknown'}</span>
                    <span style={{ color: '#94a3b8' }}>{p.requests} req · {Math.round(p.avg_latency_ms)}ms</span>
                  </div>
                  <div style={{ height: 8, background: '#334155', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(p.requests / maxReq) * 100}%`, background: providerColor(p.provider), borderRadius: 4, transition: 'width 0.4s' }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Top Models Table */}
          {byModel.length > 0 && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #334155' }}>
              <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#94a3b8' }}>Top modele</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #334155' }}>
                      {['Model', 'Req', 'Latency', 'Tok IN', 'Tok OUT'].map(h => (
                        <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byModel.map(m => (
                      <tr key={m.model} style={{ borderBottom: '1px solid #1e293b' }}>
                        <td style={{ padding: '6px 8px', color: '#7dd3fc', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.model}</td>
                        <td style={{ padding: '6px 8px', color: '#e2e8f0' }}>{m.requests}</td>
                        <td style={{ padding: '6px 8px', color: '#e2e8f0' }}>{Math.round(m.avg_latency_ms)}ms</td>
                        <td style={{ padding: '6px 8px', color: '#e2e8f0' }}>{(m.tokens_in || 0).toLocaleString()}</td>
                        <td style={{ padding: '6px 8px', color: '#e2e8f0' }}>{(m.tokens_out || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Rate Limit Manager */}
      <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 20, border: '1px solid #7c3aed' }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#a78bfa' }}>⚙️ Limity Rate / Quota</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {/* Provider */}
          <div>
            <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Provider</label>
            <select
              value={limitForm.provider}
              onChange={e => setLimitForm(p => ({ ...p, provider: e.target.value }))}
              style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
            >
              {KNOWN_PROVIDERS.map(pr => <option key={pr} value={pr}>{pr}</option>)}
            </select>
          </div>
          {/* Reset period */}
          <div>
            <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Reset period</label>
            <select
              value={limitForm.reset_period}
              onChange={e => setLimitForm(p => ({ ...p, reset_period: e.target.value }))}
              style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
            >
              <option value="minute">Per minute</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>

        {/* Rate limit fields grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
          {([
            { key: 'rpm', label: 'RPM – req/min' },
            { key: 'rpd', label: 'RPD – req/day' },
            { key: 'tpm', label: 'TPM – tokens/min' },
            { key: 'tpd', label: 'TPD – tokens/day' },
            { key: 'ash', label: 'ASH – audio sec/hr' },
            { key: 'asd', label: 'ASD – audio sec/day' },
          ] as { key: keyof LimitForm; label: string }[]).map(({ key, label }) => (
            <div key={key}>
              <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>{label}</label>
              <input placeholder="∞" {...fieldNum(key)} />
            </div>
          ))}
        </div>

        {/* Token / USD limit (legacy) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Max tokens (okres)</label>
            <input
              type="number" min={0}
              value={limitForm.max_tokens}
              onChange={e => setLimitForm(p => ({ ...p, max_tokens: parseInt(e.target.value) || 0 }))}
              style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: '#94a3b8', display: 'block', marginBottom: 4 }}>Max USD (okres)</label>
            <input
              type="number" min={0} step={0.5}
              value={limitForm.max_usd}
              onChange={e => setLimitForm(p => ({ ...p, max_usd: parseFloat(e.target.value) || 0 }))}
              style={{ width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleSaveLimit}
            disabled={saving}
            style={{ padding: '8px 20px', borderRadius: 8, background: '#7c3aed', color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >{saving ? 'Zapisuję…' : '💾 Zapisz limit'}</button>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('✅') ? '#22c55e' : '#f87171' }}>{saveMsg}</span>}
        </div>
      </div>

      {/* Current limits table */}
      {limits.length > 0 && (
        <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, border: '1px solid #334155' }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 14, color: '#94a3b8' }}>Aktualne limity</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Provider', 'RPM', 'RPD', 'TPM', 'TPD', 'ASH', 'ASD', 'Max Tok', 'Max $', 'Okres'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#94a3b8', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {limits.map(l => (
                  <tr key={l.provider} style={{ borderBottom: '1px solid #0f172a' }}>
                    <td style={{ padding: '6px 8px', color: providerColor(l.provider), fontWeight: 600 }}>{l.provider}</td>
                    <td style={{ padding: '6px 8px' }}>{l.rpm ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{l.rpd ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{l.tpm ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{l.tpd ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{l.ash ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{l.asd ?? '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{(l.max_tokens || 0).toLocaleString()}</td>
                    <td style={{ padding: '6px 8px' }}>${l.max_usd}</td>
                    <td style={{ padding: '6px 8px', color: '#94a3b8' }}>{l.reset_period}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemetryDashboard;
